import { useContext, useState } from 'react'
import firebase from '../../../firebase/compat'
import { db } from '../../../firebase/config'
import { useFirestoreGeneralOrderBy, useFirestoreUsers } from '../../../firebase/useFirestore'
import { Auth } from '../../../StateManagment/Auth'
import { SavedIcon } from '../../../StateManagment/SavedIcon'
import Table from '../../common/table/Table.jsx'
import TableCell from '../../common/table/TableCell.jsx'
import DeleteModal from '../../common/DeleteModal'
import Tooltip from '../../common/Tooltip'
import ReadOnlyGuard from '../../common/ReadOnlyGuard'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import softDeleteDoc from '../../../helpers/softDeleteDoc'
import isTaskDone from '../../../helpers/portfolio/isTaskDone'
import ProjectTaskModal from './ProjectTaskModal.jsx'

const DATE_OPTIONS = { year: 'numeric', month: 'numeric', day: 'numeric' }
const TIME_OPTIONS = { hour: '2-digit', minute: '2-digit' }

/**
 * Leesbare weergave van wanneer een afspraak staat. Zonder eindtijd is het een
 * hele dag; die krijgt geen tijdstip te zien. Een afspraak zonder datum is een
 * los actiepunt — die staat wel in de lijst, maar nog niet in de agenda.
 *
 * @param {{Timestamp?: {toDate?: () => Date}, TimestampEnd?: {toDate?: () => Date}|null}} task
 * @returns {string}
 */
const describeWhen = (task) => {
    if (!task?.Timestamp?.toDate) return 'Nog geen datum'

    const start = task.Timestamp.toDate()
    const date = start.toLocaleDateString('nl-NL', DATE_OPTIONS)

    if (!task.TimestampEnd?.toDate) return `${date} — hele dag`

    const from = start.toLocaleTimeString('nl-NL', TIME_OPTIONS)
    const to = task.TimestampEnd.toDate().toLocaleTimeString('nl-NL', TIME_OPTIONS)

    return `${date} ${from}–${to}`
}

/**
 * Afspraken die de beheertenant bij een klantproject vastlegt. De documenten
 * leven in de eigen omgeving als gewone agenda-taken (`Type: 'projectFollowUp'`)
 * en verschijnen daardoor vanzelf in de agenda.
 *
 * AI-CONTEXT: See docs/features/deccos-projectafspraken.md
 *
 * @param {object} props
 * @param {string} [props.projectId] - CompagnyID van het klantproject
 * @param {string} [props.projectName] - Naam van het project, gaat mee op nieuwe afspraken
 */
const ProjectTasks = ({ projectId, projectName = '' }) => {
    // Context
    const [auth] = useContext(Auth)
    const [, setSaved] = useContext(SavedIcon)

    // State
    const [modalOpen, setModalOpen] = useState(false)
    const [activeTask, setActiveTask] = useState(/** @type {object|null} */ (null))
    // Elke opening krijgt een eigen nummer, zodat het venster ook bij twee keer
    // 'toevoegen' achter elkaar leeg begint.
    const [openCount, setOpenCount] = useState(0)
    const [deleteModal, setDeleteModal] = useState(false)
    const [deleteDocid, setDeleteDocid] = useState('')
    const [deleteName, setDeleteName] = useState('deze afspraak')

    // Firestore — '' is het signaal voor "nog geen project", dan draait er geen query
    const tasks = useFirestoreGeneralOrderBy('Tasks', 'ProjectID', projectId || '', 'Timestamp', 'desc')
    const users = useFirestoreUsers()

    const rows = Array.isArray(tasks) ? tasks : []
    const teamUsers = Array.isArray(users) ? users : []

    const nameFor = (userId) => {
        const user = teamUsers.find((candidate) => candidate.ID === userId)
        return user?.UserName || user?.Email || userId
    }

    const openCreateModal = () => {
        setActiveTask(null)
        setOpenCount((count) => count + 1)
        setModalOpen(true)
    }

    const openEditModal = (task) => {
        setActiveTask(task)
        setOpenCount((count) => count + 1)
        setModalOpen(true)
    }

    // Afvinken schrijft een echte boolean plus het moment, zodat later te zien
    // is wanneer een afspraak is afgehandeld.
    const toggleDone = async (task) => {
        const done = !isTaskDone(task)

        await db.collection('Tasks').doc(task.docid).update({
            Done: done,
            DoneAt: done ? firebase.firestore.FieldValue.serverTimestamp() : null,
            DoneBy: done ? (auth?.ID || '') : '',
        })

        setSaved('flex')
    }

    const deleteModalHandler = (e) => {
        setDeleteDocid(e.currentTarget.dataset.docid)
        setDeleteName(e.currentTarget.dataset.deletename || 'deze afspraak')
        setDeleteModal(true)
    }

    const deleteTask = async () => {
        await softDeleteDoc({ collection: 'Tasks', docid: deleteDocid, authId: auth?.ID })
        setSaved('flex')
    }

    return (
        <div className="project-afspraken-block">
            <ReadOnlyGuard edit="tasks.edit">
                {(canEdit) => (
                    <Table
                        columns={[
                            { key: 'done', label: 'AF', searchable: false },
                            { key: 'title', label: 'AFSPRAAK' },
                            { key: 'when', label: 'WANNEER', searchable: false },
                            { key: 'who', label: 'TOEGEWEZEN AAN', searchable: false },
                            { key: 'description', label: 'OMSCHRIJVING', searchable: false },
                            { key: 'delete', label: 'VERWIJDEREN', searchable: false },
                        ]}
                        items={rows}
                        getItemKey={(task) => task.ID}
                        renderRow={(task) => (
                            <>
                                <TableCell>
                                    <input
                                        type="checkbox"
                                        className="project-afspraken-done"
                                        checked={isTaskDone(task)}
                                        onChange={() => toggleDone(task)}
                                        disabled={!canEdit}
                                        aria-label={`${task.Title || 'Naamloze afspraak'} afvinken`}
                                    />
                                </TableCell>
                                <TableCell>
                                    <button
                                        type="button"
                                        className={`project-afspraken-link${isTaskDone(task) ? ' project-afspraken-link--done' : ''}`}
                                        onClick={() => openEditModal(task)}
                                    >
                                        {task.Title || 'Naamloze afspraak'}
                                    </button>
                                </TableCell>
                                <TableCell>{describeWhen(task)}</TableCell>
                                <TableCell>
                                    {Array.isArray(task.TeamMembers) && task.TeamMembers.length > 0
                                        ? task.TeamMembers.map(nameFor).join(', ')
                                        : 'Niemand'}
                                </TableCell>
                                <TableCell>
                                    <p>{task.Description || ''}</p>
                                </TableCell>
                                <TableCell>
                                    {canEdit && (
                                        <Tooltip content={'Afspraak verwijderen'} top="-60px" width={'30px'}>
                                            <DeleteOutlinedIcon
                                                className="table-delete-icon"
                                                data-docid={task.docid}
                                                data-deletename={task.Title}
                                                onClick={deleteModalHandler}
                                            />
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </>
                        )}
                        searchAccessor={(task) => task?.Title || ''}
                        searchPlaceholder="Zoeken op afspraak"
                        emptyMessage="Nog geen afspraken vastgelegd."
                        addRow={canEdit ? {
                            toolTipContent: 'Afspraak toevoegen',
                            onClick: openCreateModal,
                        } : undefined}
                    />
                )}
            </ReadOnlyGuard>

            <ProjectTaskModal
                modalOpen={modalOpen}
                setModalOpen={setModalOpen}
                projectId={projectId}
                projectName={projectName}
                task={activeTask}
                position={rows.length + 1}
                openCount={openCount}
                users={teamUsers}
            />

            <DeleteModal
                deleteModal={deleteModal}
                setDeleteModal={setDeleteModal}
                deleteItem={deleteTask}
                deleteName={deleteName}
                softDelete
            />
        </div>
    )
}

export default ProjectTasks
